interface SafetyScoreProps {
  safetyLevel: number;
  safetyGrade: string;
  lastInspection: string;
  nextInspection: string;
  insurance: { valid: boolean; expiry: string; company: string };
  devices: { status: string; nextCalibration: string }[];
}

const inspectionHistory = [
  { date: "2025-11-20", result: "合格", inspector: "李工", issues: "无", level: "A" },
  { date: "2025-05-18", result: "合格", inspector: "王工", issues: "燃气软管轻微老化，已更换", level: "A" },
  { date: "2024-11-15", result: "合格", inspector: "张工", issues: "无", level: "A+" },
  { date: "2024-05-10", result: "合格", inspector: "李工", issues: "无", level: "A+" },
];

const riskItems = [
  { name: "燃气表状态", status: "正常", score: 25, maxScore: 25, icon: "ri-dashboard-line" },
  { name: "报警器状态", status: "正常", score: 20, maxScore: 20, icon: "ri-alarm-warning-line" },
  { name: "安检时效", status: "正常", score: 20, maxScore: 20, icon: "ri-shield-check-line" },
  { name: "保险状态", status: "正常", score: 15, maxScore: 15, icon: "ri-shield-line" },
  { name: "设备校验", status: "正常", score: 12, maxScore: 15, icon: "ri-tools-line" },
  { name: "用气规范", status: "良好", score: 5, maxScore: 5, icon: "ri-fire-line" },
];

export default function SafetyScore({
  safetyLevel,
  safetyGrade,
  lastInspection,
  nextInspection,
  insurance,
}: SafetyScoreProps) {
  const gradeConfig: Record<string, { color: string; bg: string; ring: string; label: string }> = {
    "A+": { color: "text-green-600", bg: "bg-green-50", ring: "#22c55e", label: "优秀" },
    "A":  { color: "text-teal-600",  bg: "bg-teal-50",  ring: "#14b8a6", label: "良好" },
    "B":  { color: "text-amber-600", bg: "bg-amber-50", ring: "#f59e0b", label: "一般" },
    "C":  { color: "text-red-600",   bg: "bg-red-50",   ring: "#ef4444", label: "较差" },
  };
  const cfg = gradeConfig[safetyGrade] || gradeConfig["A"];

  // SVG gauge
  const radius = 54;
  const cx = 70;
  const cy = 70;
  const startAngle = -210;
  const endAngle = 30;
  const totalAngle = endAngle - startAngle;
  const scoreAngle = startAngle + (safetyLevel / 100) * totalAngle;

  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const arcPath = (start: number, end: number, r: number) => {
    const s = { x: cx + r * Math.cos(toRad(start)), y: cy + r * Math.sin(toRad(start)) };
    const e = { x: cx + r * Math.cos(toRad(end)), y: cy + r * Math.sin(toRad(end)) };
    const large = end - start > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
  };

  const daysToNextInspection = Math.ceil(
    (new Date(nextInspection).getTime() - new Date("2026-04-10").getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-800">安全评分</h3>
        <button className="text-xs text-[#E8531A] border border-[#E8531A]/30 px-2.5 py-1 rounded-lg hover:bg-orange-50 cursor-pointer whitespace-nowrap">
          生成安检报告
        </button>
      </div>

      <div className="flex gap-5">
        {/* Gauge */}
        <div className="flex flex-col items-center flex-shrink-0">
          <svg width="140" height="100" viewBox="0 0 140 100">
            {/* Background arc */}
            <path d={arcPath(startAngle, endAngle, radius)} fill="none" stroke="#f3f4f6" strokeWidth="10" strokeLinecap="round" />
            {/* Score arc */}
            <path d={arcPath(startAngle, scoreAngle, radius)} fill="none" stroke={cfg.ring} strokeWidth="10" strokeLinecap="round" />
            {/* Center text */}
            <text x={cx} y={cy - 4} textAnchor="middle" fontSize="22" fontWeight="bold" fill="#111827">{safetyLevel}</text>
            <text x={cx} y={cy + 12} textAnchor="middle" fontSize="10" fill="#9ca3af">安全评分</text>
          </svg>
          <div className={`px-4 py-1 rounded-full text-sm font-bold ${cfg.color} ${cfg.bg}`}>
            {safetyGrade} 级 · {cfg.label}
          </div>
        </div>

        {/* Risk items */}
        <div className="flex-1 space-y-2">
          {riskItems.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-gray-400 text-xs`}></i>
              </div>
              <span className="text-xs text-gray-600 w-20 flex-shrink-0">{item.name}</span>
              <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${(item.score / item.maxScore) * 100}%`,
                    backgroundColor: item.score === item.maxScore ? "#22c55e" : "#f59e0b",
                  }}
                ></div>
              </div>
              <span className="text-xs text-gray-500 w-12 text-right flex-shrink-0">{item.score}/{item.maxScore}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Inspection info */}
      <div className="mt-4 pt-4 border-t border-gray-50 grid grid-cols-3 gap-3">
        <div className={`rounded-lg p-3 ${daysToNextInspection < 30 ? "bg-amber-50" : "bg-gray-50"}`}>
          <p className="text-xs text-gray-400">上次安检</p>
          <p className="text-sm font-medium text-gray-800 mt-0.5">{lastInspection}</p>
        </div>
        <div className={`rounded-lg p-3 ${daysToNextInspection < 30 ? "bg-amber-50" : "bg-gray-50"}`}>
          <p className="text-xs text-gray-400">下次安检</p>
          <p className={`text-sm font-medium mt-0.5 ${daysToNextInspection < 30 ? "text-amber-600 font-bold" : "text-gray-800"}`}>
            {nextInspection}
          </p>
          {daysToNextInspection < 30 && (
            <p className="text-xs text-amber-500 mt-0.5">还有 {daysToNextInspection} 天</p>
          )}
        </div>
        <div className={`rounded-lg p-3 ${insurance.valid ? "bg-green-50" : "bg-red-50"}`}>
          <p className="text-xs text-gray-400">保险状态</p>
          <p className={`text-sm font-medium mt-0.5 ${insurance.valid ? "text-green-600" : "text-red-600"}`}>
            {insurance.valid ? "有效" : "已过期"}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">{insurance.expiry} 到期</p>
        </div>
      </div>

      {/* Inspection history */}
      <div className="mt-4 pt-4 border-t border-gray-50">
        <p className="text-xs text-gray-400 mb-3">安检历史记录</p>
        <div className="space-y-2">
          {inspectionHistory.map((rec, i) => (
            <div key={i} className="flex items-center gap-3 text-xs">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0"></div>
              <span className="text-gray-400 w-24 flex-shrink-0">{rec.date}</span>
              <span className="text-green-600 font-medium w-10 flex-shrink-0">{rec.result}</span>
              <span className="text-gray-500 flex-1 truncate">{rec.issues}</span>
              <span className="text-gray-400 flex-shrink-0">检查员：{rec.inspector}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
